import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Typography, Card, CardContent, Button, CircularProgress, Alert, Chip,
  Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import AthleteCard from '../../components/athletes/AthleteCard';
import { getRosteredAthlete } from '../../services/rosterService';
import { listMeasurements, listTestingSessions, fetchTestTypes } from '../../services/athleteService';
import { getAthleteCardPhoto, getAthleteCardPhotoUrl } from '../../services/athletePhotoService';
import { getTeam } from '../../services/teamService';
import { derivePersonalBests } from '../../utils/testStats';
import { formatHeight } from '../../utils/heightUtils';
import { positionLabel } from '../../utils/footballPositions';
import { formatTimestamp } from '../../utils/timestampFormat';

function age(dob) {
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (365.25 * 24 * 60 * 60 * 1000));
}

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString();
}
// team_athletes.added_at (012) is a real, trustworthy timestamptz — not a
// fabricated stand-in — set once, server-side, exactly when this roster
// row is created (`default now()`). It is NOT a plain `date` like
// measurement_date, so it must NOT go through formatDate() above: that
// helper appends 'T00:00:00' to build a LOCAL midnight from a date-only
// string, which corrupts an already-fully-qualified timestamptz string
// (e.g. "2026-08-01T14:23:00+00:00T00:00:00") into an unparseable one —
// this was the exact cause of "Joined Roster — Invalid Date". See
// timestampFormat.js for the dedicated, separately-tested helper.

const GEAR_LABELS = { no_gear: 'No Gear', with_gear: 'With Gear', unknown: 'Gear Unknown' };

function Row({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.05)', pb: 1 }}>
      <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>{label}</Typography>
      <Typography sx={{ fontWeight: 600 }}>{value}</Typography>
    </Box>
  );
}

/**
 * Coach-facing rostered-athlete view — separate from AthleteProfilePage
 * on purpose (012). Read-only: no Edit Athlete, no measurement add/edit/
 * remove, no gear management, nothing guardian-only reachable from here.
 * Visible only while the caller is an active team_staff member of a team
 * this athlete has an active team_athletes row on — RLS (012) is what
 * actually enforces that; this page just renders what comes back.
 *
 * Deliberately shows age, not raw date of birth, and never ZIP — county/
 * school district/state are the location context shown, per explicit
 * instruction. Deliberately does NOT compute "Estimated Body Weight" for
 * with_gear rows — that requires athlete_gear_weights, which stays
 * guardian-only; this page never fetches gear-weight data at all, so
 * there's nothing to derive it from, by construction, not by a filter.
 *
 * Future home for team-viewable performance, team testing, goals, PBs,
 * and training recommendations (012 ships only the read-only V1 fields
 * below — none of that yet).
 */
function RosteredAthleteInner() {
  const { teamId, teamAthleteId } = useParams();
  const navigate = useNavigate();
  const [row, setRow] = useState(null);
  const [team, setTeam] = useState(null);
  const [measurements, setMeasurements] = useState([]);
  // Phase C.8 — read-only for the coach context: testing history is
  // already readable here under existing Team-staff RLS (049's "team
  // staff view rostered athlete testing sessions/test attempts"
  // policies), so Career PBs can be shown on the coach Card without
  // broadening any authorization — no new RLS, no new RPC.
  const [sessions, setSessions] = useState([]);
  const [testTypes, setTestTypes] = useState([]);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoLoading, setPhotoLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const r = await getRosteredAthlete(teamAthleteId);
    const [t, m, s, tt] = await Promise.all([
      getTeam(r.team_id), listMeasurements(r.athlete.id),
      listTestingSessions(r.athlete.id), fetchTestTypes(),
    ]);
    setRow(r);
    setTeam(t);
    setMeasurements(m);
    setSessions(s);
    setTestTypes(tt);
  }, [teamAthleteId]);

  useEffect(() => {
    load().catch(e => setError(e.message)).finally(() => setLoading(false));
  }, [load]);

  const personalBests = useMemo(() => derivePersonalBests(sessions, testTypes), [sessions, testTypes]);
  // Phase C.8 — this membership's OWN Team -> Division -> Season chain
  // (rosterService.getRosteredAthlete(), widened for this phase) — never
  // a second, independently-resolved season. Coach access to the Card
  // photo is read-only; there is no upload path on this page at all.
  const cardSeasonId = row?.team?.division?.season?.id || null;

  useEffect(() => {
    let cancelled = false;
    if (!row?.athlete?.id || !cardSeasonId) {
      setPhotoUrl(null);
      return undefined;
    }
    setPhotoLoading(true);
    (async () => {
      const photoRow = await getAthleteCardPhoto(row.athlete.id, cardSeasonId);
      if (cancelled) return;
      setPhotoUrl(photoRow ? await getAthleteCardPhotoUrl(photoRow.storage_path) : null);
    })()
      .catch(() => { if (!cancelled) setPhotoUrl(null); })
      .finally(() => { if (!cancelled) setPhotoLoading(false); });
    return () => { cancelled = true; };
  }, [row?.athlete?.id, cardSeasonId]);

  if (loading) return <Box sx={{ py: 10, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>;
  if (error) return <Container maxWidth="sm" sx={{ py: 6 }}><Alert severity="error">{error}</Alert></Container>;
  if (!row || !row.athlete) return null;

  const athlete = row.athlete;
  const athleteAge = age(athlete.date_of_birth);
  const countyState = athlete.county?.name && athlete.state_code
    ? `${athlete.county.name} County, ${athlete.state_code}`
    : athlete.county?.name || athlete.state_code || '—';
  const positions = [row.primary_position, row.secondary_position].filter(Boolean).map(positionLabel);
  // Phase C.6 — this specific membership's own season_grade/jersey_number
  // (047), never the athlete's live/current grade — this page is already
  // scoped to ONE roster row, so there's no ambiguity about which season
  // this is describing.

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate(`/my-teams/${teamId}/roster`)} sx={{ textTransform: 'none', color: '#6a8aa8', mb: 1, pl: 0 }}>
        ← Roster
      </Button>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>{athlete.first_name} {athlete.last_name}</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
            <Chip size="small" label={`Grade ${row.season_grade || '—'}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />
            {row.jersey_number !== null && row.jersey_number !== undefined && (
              <Chip size="small" label={`#${row.jersey_number}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />
            )}
            {athleteAge !== null && <Chip size="small" label={`Age ${athleteAge}`} sx={{ bgcolor: '#131318', color: '#9eb8d0' }} />}
            {positions.map(p => <Chip key={p} size="small" label={p} sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />)}
            <Chip
              size="small" label={row.status === 'active' ? 'Active on Roster' : 'Inactive'}
              sx={{ bgcolor: row.status === 'active' ? '#0a2a14' : '#2a1405', color: row.status === 'active' ? '#2ea84b' : '#f5a623' }}
            />
          </Box>
        </Box>
      </Box>

      {/* Phase C.8 — the SAME canonical AthleteCard the guardian profile
          renders. Season identity comes from THIS membership (row), not
          the athlete's live/current grade. Read-only: no canUpload prop
          is passed, so no upload action ever renders here — the coach
          never gains guardian-only write capability. */}
      <Box sx={{ mt: 3 }}>
        <AthleteCard
          athlete={athlete}
          membership={row}
          personalBests={personalBests}
          photoUrl={photoUrl}
          photoLoading={photoLoading}
          canUpload={false}
        />
      </Box>

      <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Row label="Team" value={team?.team_name || '—'} />
            <Row label="Grade (this season)" value={row.season_grade || '—'} />
            <Row label="Jersey Number" value={row.jersey_number ?? '—'} />
            <Row label="Height" value={athlete.height_in ? formatHeight(athlete.height_in) : '—'} />
            <Row label="Weight" value={athlete.weight_lb ? `${athlete.weight_lb} lb` : '—'} />
            <Row label="School District" value={athlete.school_district?.name || '—'} />
            <Row label="County / State" value={countyState} />
            <Row label="Joined Roster" value={formatTimestamp(row.added_at)} />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mt: 3, backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
        <CardContent>
          <Typography sx={{ fontWeight: 800, mb: 0.5 }}>Measurement History</Typography>
          <Typography sx={{ color: '#4a6080', fontSize: '0.75rem', mb: 2 }}>
            Read-only — recorded by the athlete's guardian.
          </Typography>
          {measurements.length === 0 ? (
            <Typography sx={{ color: '#6a8aa8' }}>No measurements recorded yet.</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Height</TableCell>
                    <TableCell>Weight</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {measurements.map(m => (
                    <TableRow key={m.id}>
                      <TableCell>{formatDate(m.measurement_date)}</TableCell>
                      <TableCell>{m.height_in ? formatHeight(m.height_in) : '—'}</TableCell>
                      <TableCell>
                        {m.weight_lb ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            <span>{m.weight_lb} lb</span>
                            <Chip
                              size="small" label={GEAR_LABELS[m.weight_gear_status] || 'Gear Unknown'}
                              sx={{
                                fontSize: '0.65rem', height: 18,
                                bgcolor: m.weight_gear_status === 'with_gear' ? '#2a1405' : m.weight_gear_status === 'unknown' ? '#131318' : '#0a2a14',
                                color: m.weight_gear_status === 'with_gear' ? '#f5a623' : m.weight_gear_status === 'unknown' ? '#6a8aa8' : '#2ea84b',
                              }}
                            />
                          </Box>
                        ) : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}

export default function RosteredAthletePage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><RosteredAthleteInner /></RequireAuth>
    </Box>
  );
}
